import { Link } from "react-router-dom";
import { Sprout, Users, Sparkles, UsersRound, UserRound, ShieldCheck, CalendarDays, MessageCircle, Image, Lock, Heart } from "lucide-react";

const SECTIONS = [
  {
    icon: Sprout,
    title: "What's Growing",
    tagline: "Your goals, owned by you — never scored, never ranked.",
    items: [
      "Intentions — name what's growing for you, with private notes only you can see",
      "One small next step at a time, with optional target dates",
      "Checklist sub-steps you can add, tick, rename, and edit anytime",
      "Pause, resume, or close intentions whenever life shifts",
      "Gentle reflections when you complete a step — create the next one, keep going, or rest",
    ],
  },
  {
    icon: Users,
    title: "Pods",
    tagline: "Small circles of people you trust. Conversation stays in your own group chats.",
    items: [
      "Start a pod in seconds — name it, pick purposes (including Growing money), set a shared intention",
      "Instant invite link — friends join Bouquet and land in your pod in one step",
      "Share a next step when you want witnesses — with how you'd like to be supported",
      "Gentle responses only: cheer them on, offer to check in, lend a hand — no comments section, no scores",
      "Tap any shared step to see the whole picture — intention, target date, checklist",
      "Prompts to drop a note in your group chat when someone shares something new",
      "Share tiles as beautiful image cards with your own message",
      "Pod gatherings with photos and RSVP (going / maybe / can't)",
      "Pod links — books, accounts, and tools your circle recommends",
    ],
  },
  {
    icon: Sparkles,
    title: "Moments",
    tagline: "Milestones, dinners, small wins — kept, not posted.",
    items: [
      "Preserve moments with a photo — for yourself, your pod, or your community",
      "Tap a moment to see the whole story and its reflection",
      "Edit your moments anytime; send them to your chat as an image card",
    ],
  },
  {
    icon: UsersRound,
    title: "Community",
    tagline: "The wider circle you're part of.",
    items: [
      "Your community's members, moments, and gatherings in one place",
      "Anyone in the community can plan a gathering — everyone can RSVP",
    ],
  },
  {
    icon: UserRound,
    title: "Profiles with depth",
    tagline: "There's more to you than a title.",
    items: [
      "Describe what you do in your own words",
      "What I Know — the topics, problems, and skills people come to you for",
      "My Hyphenates — leader, creator, strategist, artist… all of them",
      "What you're exploring right now — promotion, career change, entrepreneurship, creative projects…",
      "See the profiles of people you share a pod or community with — nobody else's",
    ],
  },
  {
    icon: Lock,
    title: "Private by default",
    tagline: "You decide what to share, and with whom. Always.",
    items: [
      "Intentions, notes, and reflections are yours alone until you choose otherwise",
      "No feeds, no followers, no leaderboards, no streaks, no red warnings",
      "No tracking of private content — even the admin view is counts only",
    ],
  },
];

export default function Features() {
  return (
    <div className="min-h-screen bg-background" data-testid="features-page">
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-12">
        <header className="text-center space-y-5">
          <img src="/logo-trim.png" alt="Bouquet" className="h-16 object-contain mx-auto" data-testid="features-logo" />
          <h1 className="font-display text-4xl sm:text-5xl font-medium" data-testid="features-title">Grow what matters, with your people.</h1>
          <p className="text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
            Bouquet is a person-centered growth platform: name an intention, take a small step, let your people show up for you. No feeds. No scores. Just movement.
          </p>
          <Link to="/login" data-testid="features-join"
            className="inline-block bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
            Join Bouquet
          </Link>
        </header>

        {SECTIONS.map(({ icon: Icon, title, tagline, items }) => (
          <section key={title} className="bg-card border border-border rounded-2xl p-7 space-y-4" data-testid={`features-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-sage-pale flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-medium">{title}</h2>
                <p className="text-xs text-muted-foreground">{tagline}</p>
              </div>
            </div>
            <ul className="space-y-2.5">
              {items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                  <Heart className="h-3.5 w-3.5 text-primary mt-1 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section className="text-center space-y-4 pb-6">
          <p className="font-display text-2xl font-medium">Ready when you are.</p>
          <p className="text-sm text-muted-foreground">Free to join. Your first pod takes two minutes.</p>
          <Link to="/login" data-testid="features-join-bottom"
            className="inline-block bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
            Start your Bouquet
          </Link>
        </section>
      </div>
    </div>
  );
}
